/* LAV TDMS — комплекты исполнительной документации по титулу. */
import { useState } from 'react'
import { Badge, Button, Dialog, EmptyState, Field, Input, PageHead, Select, Spinner, lifeLabel, toast, railWidth } from '@/ui'
import { Icon } from '@/ds/icons'
import { qk, useList, useSub } from '@/data/queries'
import { useCommand } from '@/data/mutations'
import { useDictRows, useMe } from '@/data/meta'
import { exportRows } from '@/lib/excel'
import type { Vendor } from '@/data/types'
import { t, type Lang } from '@/i18n/lang'

interface HandoverItem {
  doc: string
  title: string | null
  disc: string | null
  rev: string
  /** Пусто — позиция заявлена обязательной, а документа в реестре ещё нет. */
  life: string | null
  approvedOn: string | null
  hasFile: boolean
  /** 'titul' — по правилу состава, 'add' — добавлен вручную, 'required' — обязателен. */
  rule: string
  reason: string | null
  supersededBy: string | null
  ready: boolean
}

interface HandoverPackage {
  code: string
  titul: string
  title: string
  state: 'draft' | 'issued'
  /** Письмо, которым комплект передан: пусто — ещё не передавали. */
  trm?: string | null
  issuedOn: string | null
  issuedBy: string | null
  incompleteReason: string | null
  total: number
  ready: number
  missing: number
  items?: HandoverItem[]
  /** Исключённые из комплекта позиции: решение «этого не будет» обязано быть видно. */
  exempted?: { doc: string; title: string | null; reason: string | null }[]
}

/**
 * Комплект ИД: что должно быть по титулу, что готово, чего не хватает.
 *
 * <p>Состав здесь не редактируется списком — он считается по реестру. Правятся
 * только отклонения: исключить позицию (с причиной) или вернуть её обратно.
 * Иначе экран стал бы вторым реестром, который разойдётся с первым.
 */
export function Handover({ lang = 'ru' }: { lang?: Lang }) {
  const me = useMe().data
  const list = useSub<{ packages: HandoverPackage[] }>('/handover')
  const [chosen, setChosen] = useState<string>('')
  const [creating, setCreating] = useState(false)
  const packages = list.data?.packages ?? []
  const code = chosen || packages[0]?.code || ''
  const detail = useSub<HandoverPackage>(code ? '/handover/' + encodeURIComponent(code) : null)
  const pkg = detail.data

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--surface-page)' }}>
      <PageHead
        crumbs={<span>{me?.project ?? ''} <span style={{ color: 'var(--border-strong)' }}>/</span> {t(lang, 'Комплекты ИД', 'Handover')}</span>}
        title={t(lang, 'Комплекты исполнительной документации', 'Handover packages')}
        count={packages.length}
        actions={<Button variant="primary" size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => setCreating(true)}>
          {t(lang, 'Новый комплект', 'New package')}
        </Button>} />

      {creating ? <CreateDialog lang={lang} onClose={() => setCreating(false)} onCreated={setChosen} /> : null}

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <div style={{ width: railWidth(300), flex: '0 0 auto', borderRight: '1px solid var(--border-default)', background: 'var(--surface-card)', overflow: 'auto' }}>
          {list.isPending ? <div style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 8, font: 'var(--font-ui)', color: 'var(--text-subtle)' }}><Spinner size={13} />{t(lang, 'Загрузка…', 'Loading…')}</div> : null}
          {packages.map((p) => (
            <button key={p.code} onClick={() => setChosen(p.code)} className="tdms-rail"
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '11px 12px', border: 0, borderBottom: '1px solid var(--border-subtle)', background: p.code === code ? 'var(--accent-soft)' : 'transparent', boxShadow: p.code === code ? 'inset 2px 0 0 var(--accent)' : 'none', cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--text-strong)' }}>{p.code}</span>
                <Badge tone={p.state === 'issued' ? 'success' : 'neutral'}>
                  {p.state === 'issued' ? t(lang, 'Выпущен', 'Issued') : t(lang, 'Черновик', 'Draft')}
                </Badge>
              </div>
              <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginTop: 2 }}>{p.title}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                <span style={{ flex: 1, height: 4, borderRadius: 2, background: 'var(--surface-sunken)', overflow: 'hidden' }}>
                  {/* Готовность растёт, а не подменяется: после отметки и рефетча полоса доезжает до нового значения. */}
                  <span style={{ display: 'block', height: '100%', width: (p.total ? Math.round((p.ready / p.total) * 100) : 0) + '%', background: p.missing ? 'var(--amber-400)' : 'var(--green-500)', transition: 'width var(--duration-slow) var(--ease-standard)' }} />
                </span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)' }}>{p.ready}/{p.total}</span>
              </div>
            </button>
          ))}
          {!list.isPending && !packages.length ? (
            <div style={{ padding: 16, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
              {t(lang, 'Комплектов пока нет. Заведите комплект по титулу — состав соберётся из реестра.',
                    'No packages yet. Create one per titul — the contents come from the register.')}
            </div>
          ) : null}
        </div>

        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {!pkg ? <EmptyState icon="package-check" title={t(lang, 'Комплект не выбран', 'No package selected')} text={t(lang, 'Выберите комплект слева.', 'Pick a package on the left.')} />
            : <PackageBody pkg={pkg} lang={lang} />}
        </div>
      </div>
    </div>
  )
}

function PackageBody({ pkg, lang }: { pkg: HandoverPackage; lang: Lang }) {
  const [reason, setReason] = useState('')
  const [issuing, setIssuing] = useState(false)
  const items = pkg.items ?? []
  const key = '/handover/' + encodeURIComponent(pkg.code)

  const mark = useCommand<{ docNo: string; rule: string; reason?: string }>({
    path: () => key + '/items',
    body: (a) => a,
    invalidate: () => [qk.sub(key), qk.sub('/handover'), qk.all('audit')],
  })
  const unmark = useCommand<{ docNo: string }>({
    method: 'DELETE',
    path: (a) => key + '/items/' + encodeURIComponent(a.docNo),
    invalidate: () => [qk.sub(key), qk.sub('/handover'), qk.all('audit')],
  })
  const issue = useCommand<{ reason: string }, { missing: number }>({
    path: () => key + '/issue',
    body: (a) => a,
    invalidate: () => [qk.sub(key), qk.sub('/handover'), qk.all('audit')],
  })
  /* Передача комплекта: письмо собирается по СНИМКУ состава, а не по реестру
     на сегодня — им и сдают. */
  const deliver = useCommand<{ recipient: string }, { trm: string; docs: number }>({
    path: () => key + '/deliver',
    body: (a) => a,
    invalidate: () => [qk.sub(key), qk.sub('/handover'), qk.all('transmittals'), qk.all('audit')],
  })
  const [delivering, setDelivering] = useState(false)
  const [recipient, setRecipient] = useState('')
  const vendorsQuery = useList<Vendor>('vendors', { page: 0, pageSize: 200, sort: [{ id: 'code', desc: false }] })

  const issued = pkg.state === 'issued'
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface-card)' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{pkg.title}</span>
            <Badge tone={issued ? 'success' : 'neutral'}>{issued ? t(lang, 'Выпущен', 'Issued') : t(lang, 'Черновик', 'Draft')}</Badge>
          </div>
          <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginTop: 2, fontFamily: 'var(--font-mono)' }}>
            {pkg.code} · {t(lang, 'титул', 'titul')} {pkg.titul} · {pkg.ready}/{pkg.total} {t(lang, 'готово', 'ready')}
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {/* Опись комплекта: то же, что на экране, только в файле — им и
              сдают, а по экрану сверяют. */}
          <Button variant="secondary" size="sm" disabled={!items.length} iconLeft={<Icon name="download" size={14} />}
                  onClick={() => exportRows<HandoverItem>(items, [
                    ['№ документа', (r) => r.doc],
                    ['Наименование', (r) => r.title ?? ''],
                    ['Дисциплина', (r) => r.disc ?? ''],
                    ['Ревизия', (r) => r.rev],
                    ['Состояние', (r) => (r.life ? lifeLabel(r.life) : t(lang, 'не заведён', 'not created'))],
                    ['Согласован', (r) => r.approvedOn ?? ''],
                    ['Файл', (r) => (r.hasFile ? t(lang, 'есть', 'yes') : t(lang, 'нет', 'no'))],
                    ['Готов к сдаче', (r) => (r.ready ? t(lang, 'да', 'yes') : t(lang, 'нет', 'no'))],
                    ['Заменён на', (r) => r.supersededBy ?? ''],
                  ], 'handover-' + pkg.code + '.xlsx', t(lang, 'Опись', 'Inventory'), lang)}>
            {t(lang, 'Опись (xlsx)', 'Inventory (xlsx)')}
          </Button>
          {/* «Должно быть, а нет»: правило состава знало только add и exempt,
              и позицию, которой в реестре ещё не завели, выразить было нечем —
              пустое место читалось как отсутствие требования. */}
          {!issued ? (
            <Button variant="secondary" size="sm" disabled={mark.isPending} iconLeft={<Icon name="plus" size={14} />}
                    onClick={() => {
                      const no = window.prompt(t(lang, 'Номер обязательного документа', 'Required document number'))
                      if (!no || !no.trim()) return
                      const what = window.prompt(t(lang, 'Что это за документ?', 'What document is it?'))
                      if (!what || !what.trim()) return
                      mark.mutate({ docNo: no.trim(), rule: 'required', reason: what.trim() })
                    }}>
              {t(lang, 'Обязательная позиция', 'Required position')}
            </Button>
          ) : null}
          {!issued ? (
            <Button variant="primary" size="sm" iconLeft={<Icon name="package-check" size={14} />}
                    disabled={issue.isPending}
                    onClick={() => setIssuing(true)}>{t(lang, 'Выпустить комплект', 'Issue package')}</Button>
          ) : null}
          {/* Передача идёт письмом: у трансмиттала есть печатная форма, пакет
              файлов и подтверждение получения — своего механизма передачи
              комплекту не нужно. */}
          {issued && !pkg.trm ? (
            <Button variant="primary" size="sm" iconLeft={<Icon name="send" size={14} />}
                    disabled={deliver.isPending}
                    onClick={() => setDelivering(true)}>{t(lang, 'Передать трансмитталом', 'Deliver by transmittal')}</Button>
          ) : null}
          {pkg.trm ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--success-ink)' }}>
              <Icon name="check-check" size={14} />
              {t(lang, 'Передан ', 'Delivered by ')}<span style={{ fontFamily: 'var(--font-mono)' }}>{pkg.trm}</span>
            </span>
          ) : null}
        </div>
      </div>

      {/* Кому передаётся комплект: организация проекта, а не свободная строка —
          изоляция и печатная форма опираются на имя из справочника. */}
      {delivering ? (
        <Dialog open onClose={() => setDelivering(false)} title={t(lang, 'Передать комплект ИД', 'Deliver the handover package')}>
          <div style={{ width: 460, maxWidth: '100%', display: 'grid', gap: 12 }}>
            <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
              {t(lang, 'Уйдёт трансмитталом со снимком описи: получение подтверждается там же, где у любого письма.',
                    'It goes as a transmittal with the frozen inventory; receipt is acknowledged as with any letter.')}
            </div>
            <Select label={t(lang, 'Получатель', 'Recipient')} value={recipient} onChange={(e) => setRecipient(e.target.value)}>
              <option value="">{t(lang, '— организация —', '— organisation —')}</option>
              {(vendorsQuery.data?.rows ?? []).map((v) => <option key={v.code} value={v.name}>{v.name}</option>)}
            </Select>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="secondary" onClick={() => setDelivering(false)}>{t(lang, 'Отмена', 'Cancel')}</Button>
              <Button variant="primary" disabled={!recipient || deliver.isPending}
                      onClick={() => deliver.mutate({ recipient }, {
                        onSuccess: (r) => { setDelivering(false); toast({ tone: 'success',
                          title: t(lang, 'Комплект передан', 'Package delivered'),
                          text: t(lang, `${r.trm} · документов ${r.docs}`, `${r.trm} · ${r.docs} documents`) }) },
                        onError: (e) => toast({ tone: 'danger', title: t(lang, 'Не передан', 'Not delivered'), text: e.message }),
                      })}>{t(lang, 'Передать', 'Deliver')}</Button>
            </div>
          </div>
        </Dialog>
      ) : null}

      {issued && pkg.incompleteReason ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', background: 'var(--warning-soft, var(--surface-sunken))', borderBottom: '1px solid var(--border-subtle)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
          <Icon name="alert-triangle" size={14} />
          {t(lang, 'Выпущен неполным: ', 'Issued incomplete: ')}{pkg.incompleteReason}
        </div>
      ) : null}

      {issuing ? (
        <Dialog open onClose={() => setIssuing(false)} title={t(lang, 'Выпуск комплекта', 'Issue package')}>
          <div style={{ display: 'grid', gap: 12, width: 460, maxWidth: '100%' }}>
            <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
              {pkg.missing
                ? t(lang, `Не закрыто позиций: ${pkg.missing}. Неполный комплект выпускается только с причиной — она останется в журнале.`,
                       `${pkg.missing} positions are not ready. An incomplete package needs a reason — it stays in the audit log.`)
                : t(lang, 'Комплект полон. После выпуска состав не меняется.', 'The package is complete. Contents freeze after issue.')}
            </div>
            {pkg.missing ? (
              <Field label={t(lang, 'Причина неполноты', 'Reason')}>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t(lang, 'сдаём по составу первого этапа', 'first stage scope')} />
              </Field>
            ) : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="secondary" onClick={() => setIssuing(false)}>{t(lang, 'Отмена', 'Cancel')}</Button>
              <Button variant="primary" disabled={issue.isPending || (!!pkg.missing && !reason.trim())}
                      onClick={() => issue.mutate({ reason: reason.trim() }, {
                        onSuccess: (r) => {
                          setIssuing(false); setReason('')
                          toast({ tone: 'success', title: t(lang, 'Комплект выпущен', 'Package issued'),
                                  text: r.missing ? t(lang, `не закрыто позиций: ${r.missing}`, `${r.missing} positions missing`) : '' })
                        },
                      })}>
                {issue.isPending ? t(lang, 'Выпускаем…', 'Issuing…') : t(lang, 'Выпустить', 'Issue')}
              </Button>
            </div>
          </div>
        </Dialog>
      ) : null}

      <div style={{ flex: 1, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {[t(lang, 'Документ', 'Document'), t(lang, 'Наименование', 'Title'), t(lang, 'Рев.', 'Rev'),
                t(lang, 'Состояние', 'State'), t(lang, 'Готов', 'Ready'), ''].map((h, i) => (
                <th key={i} style={{ textAlign: 'left', padding: '9px 14px', font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', borderBottom: '1px solid var(--border-strong)', background: 'var(--surface-sunken)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.doc} className="lav-tr" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                <td style={{ padding: '9px 14px', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-strong)' }}>
                  {row.doc}
                  {row.rule === 'add' ? <span style={{ marginLeft: 6, font: 'var(--font-caption)', color: 'var(--accent-ink)' }}>{t(lang, 'добавлен', 'added')}</span> : null}
                  {row.rule === 'required' ? <span style={{ marginLeft: 6, font: 'var(--font-caption)', color: 'var(--amber-600, var(--text-subtle))' }}>{t(lang, 'обязателен', 'required')}</span> : null}
                </td>
                <td style={{ padding: '9px 14px', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
                  {row.title}
                  {/* Замена важнее наименования: в комплект обязана попасть
                      действующая редакция, а не отменённая. */}
                  {row.supersededBy ? <span style={{ marginLeft: 8, font: 'var(--font-caption)', color: 'var(--danger-ink)' }}>{t(lang, 'заменён на ', 'superseded by ')}{row.supersededBy}</span> : null}
                </td>
                <td style={{ padding: '9px 14px', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)' }}>{row.rev}</td>
                <td style={{ padding: '9px 14px', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
                  {/* Состояние — названием, тем же, что печатает бейдж реестра:
                      опись уходит в приёмочную комиссию, и «afd» в колонке
                      «Состояние» читается там как брак. */}
                  {row.life ? lifeLabel(row.life) : t(lang, 'не заведён', 'not created')}
                </td>
                <td style={{ padding: '9px 14px' }}>
                  {row.ready
                    ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--green-600)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)' }}><Icon name="check-check" size={14} />{t(lang, 'готов', 'ready')}</span>
                    : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-subtle)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)' }}>
                        <Icon name="clock" size={14} />{!row.life ? t(lang, 'нет в реестре', 'not in the register')
                          : row.hasFile ? t(lang, 'не согласован', 'not approved') : t(lang, 'нет файла', 'no file')}
                      </span>}
                </td>
                <td style={{ padding: '9px 14px', textAlign: 'right' }}>
                  {/* Заявленную обязательную позицию исключают снятием
                      отклонения: «исключить» требует строки реестра, которой у
                      неё как раз и нет. */}
                  {!issued && row.rule === 'required' ? (
                    <Button variant="secondary" size="sm" disabled={unmark.isPending}
                            onClick={() => unmark.mutate({ docNo: row.doc })}>
                      {t(lang, 'Снять требование', 'Drop requirement')}
                    </Button>
                  ) : !issued ? (
                    <Button variant="secondary" size="sm" disabled={mark.isPending}
                            onClick={() => {
                              const why = window.prompt(t(lang, 'Почему позиция не нужна в комплекте?', 'Why is this position not needed?'))
                              if (why && why.trim()) mark.mutate({ docNo: row.doc, rule: 'exempt', reason: why.trim() })
                            }}>
                      {t(lang, 'Исключить', 'Exempt')}
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!items.length ? (
          <div style={{ padding: 20, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
            {t(lang, 'В титуле нет документов — состав пуст.', 'No documents under this titul — the package is empty.')}
          </div>
        ) : null}
        {/* Исключённые позиции показываются отдельно: решение «этого не будет»
            обязано быть видно, иначе комплект выглядит полным по построению. */}
        <ExemptedNote rows={pkg.exempted ?? []} lang={lang} onRestore={(doc) => unmark.mutate({ docNo: doc })} />
      </div>
    </>
  )
}

/** Исключённые позиции приходят вместе с комплектом: это часть ответа о его составе. */
function ExemptedNote({ rows, lang, onRestore }: { rows: { doc: string; reason: string | null }[]; lang: Lang; onRestore: (doc: string) => void }) {
  if (!rows.length) return null
  return (
    <div style={{ padding: '14px 16px', borderTop: '1px solid var(--border-subtle)' }}>
      <div style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', marginBottom: 8 }}>
        {t(lang, 'Исключены из комплекта', 'Exempted from the package')}
      </div>
      <div style={{ display: 'grid', gap: 6 }}>
        {rows.map((r) => (
          <div key={r.doc} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-strong)' }}>{r.doc}</span>
            <span style={{ flex: 1, minWidth: 0, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>{r.reason}</span>
            <Button variant="secondary" size="sm" onClick={() => onRestore(r.doc)}>{t(lang, 'Вернуть', 'Restore')}</Button>
          </div>
        ))}
      </div>
    </div>
  )
}

function CreateDialog({ lang, onClose, onCreated }: { lang: Lang; onClose: () => void; onCreated: (code: string) => void }) {
  const tituls = useDictRows('titul')
  const [form, setForm] = useState({ code: '', titul: '', title: '' })
  const create = useCommand<typeof form, { code: string }>({
    path: () => '/handover',
    body: (a) => a,
    invalidate: () => [qk.sub('/handover'), qk.all('audit')],
  })
  const ready = form.code.trim() !== '' && form.titul !== ''
  return (
    <Dialog open onClose={onClose} title={t(lang, 'Новый комплект ИД', 'New handover package')}>
      <div style={{ display: 'grid', gap: 12, width: 460, maxWidth: '100%' }}>
        <Field label={t(lang, 'Код комплекта', 'Package code')}>
          <Input value={form.code} mono onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} placeholder="ИД-2100" />
        </Field>
        <Field label={t(lang, 'Титул', 'Titul')}>
          <Select value={form.titul} onChange={(e) => setForm((f) => ({ ...f, titul: e.target.value }))}>
            <option value="">{t(lang, '— выберите титул', '— pick a titul')}</option>
            {tituls.map((row) => <option key={row.code} value={row.code}>{row.code} — {row.name}</option>)}
          </Select>
        </Field>
        <Field label={t(lang, 'Наименование', 'Title')}>
          <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder={t(lang, 'Комплект ИД по титулу', 'Handover package')} />
        </Field>
        <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
          {t(lang, 'Состав соберётся из реестра по титулу. Вручную правятся только исключения.',
                'Contents come from the register by titul. Only exceptions are edited by hand.')}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
          <Button variant="primary" disabled={!ready || create.isPending}
                  onClick={() => create.mutate(form, { onSuccess: (r) => { onCreated(r.code); onClose() } })}>
            {create.isPending ? t(lang, 'Создаём…', 'Creating…') : t(lang, 'Создать', 'Create')}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
